/**
 * Section Header With Icon Component
 * Reusable header component that displays an icon with a title
 * Used across Overview sections for consistent styling
 *
 * Best Practice: Uses regular <img> tag for SVG icons instead of Next.js Image
 * because SVGs don't need optimization and load faster from browser cache
 */

import React from 'react';
import {
  typographyColors,
  spacing,
  typography,
  fontPrimary,
} from '@/design-system/utils';

export interface SectionHeaderWithIconProps {
  icon: string;
  iconAlt: string;
  title: string;
  iconSize?: number;
  className?: string;
}

export const SectionHeaderWithIcon: React.FC<SectionHeaderWithIconProps> = ({
  icon,
  iconAlt,
  title,
  iconSize = 20,
  className,
}) => {
  return (
    <div
      className={className}
      style={{
        display: 'flex',
        gap: spacing[1.5], // 6px gap between icon and title
        alignItems: 'center',
      }}
    >
      {/* Icon - Using regular img tag for SVG icons (best practice) */}
      {/* eslint-disable-next-line @next/next/no-img-element -- SVG icon, fixed size */}
      <img
        src={icon}
        alt={iconAlt}
        width={iconSize}
        height={iconSize}
        style={{
          display: 'block',
          flexShrink: 0,
        }}
        loading='eager' // Load immediately since it's above the fold
      />
      {/* Title */}
      <p
        style={{
          fontFamily: fontPrimary,
          fontSize: typography.size.lg,
          fontWeight: typography.weight.semibold,
          color: typographyColors.primary,
          margin: 0,
        }}
      >
        {title}
      </p>
    </div>
  );
};
